import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  CatalogField,
  CatalogService,
} from '@services/catalog.service';
import { NotificationService } from '@services/notification.service';

export interface CatalogImportDialogData {
  vendorId: string;
  vendorName?: string;
  catalogRefreshMonths?: number;
  lastCatalogImportAt?: string | Date;
  nextCatalogDueAt?: string | Date;
}

@Component({
  selector: 'app-catalog-import-dialog',
  templateUrl: './catalog-import-dialog.component.html',
  styleUrls: ['./catalog-import-dialog.component.css'],
})
export class CatalogImportDialogComponent implements OnInit {
  step: 'upload' | 'map' | 'preview' | 'done' = 'upload';
  fields: CatalogField[] = [];
  file: File = null;
  fieldMap: Record<string, string> = {};
  headers: string[] = [];
  preview: any = null;
  history: any[] = [];
  refreshMonths = 3;
  acceptAllChanges = false;
  acceptKeys: Record<string, boolean> = {};
  busy = false;
  result: any = null;

  constructor(
    public dialogRef: MatDialogRef<CatalogImportDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CatalogImportDialogData,
    private catalog: CatalogService,
    private notification: NotificationService
  ) {
    this.refreshMonths = Number(data.catalogRefreshMonths) || 3;
  }

  ngOnInit(): void {
    this.catalog.fields().subscribe((fields) => {
      this.fields = fields || [];
    });
    this.reloadHistory();
  }

  reloadHistory(): void {
    this.catalog.history(this.data.vendorId).subscribe((rows) => {
      this.history = rows || [];
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) {
      return;
    }
    this.file = file;
    this.runPreview({});
  }

  runPreview(fieldMap: Record<string, string>): void {
    if (!this.file) {
      return;
    }
    this.busy = true;
    this.catalog.preview(this.data.vendorId, this.file, fieldMap).subscribe({
      next: (preview) => {
        this.busy = false;
        this.preview = preview;
        this.headers = preview?.headers || [];
        this.fieldMap = {
          ...(preview?.savedMap || {}),
          ...(preview?.guessedMap || {}),
          ...(fieldMap || {}),
        };
        this.refreshMonths =
          Number(preview?.catalogRefreshMonths) || this.refreshMonths;
        this.step = Object.keys(fieldMap || {}).length ? 'preview' : 'map';
        (preview?.preview?.changed || []).forEach((row) => {
          if (row.key != null) {
            this.acceptKeys[row.key] = this.acceptAllChanges;
          }
        });
      },
      error: (err) => {
        this.busy = false;
        this.notification.show(
          'error',
          err?.error?.message || 'Could not read that catalog file'
        );
      },
    });
  }

  remap(): void {
    this.runPreview(this.fieldMap);
  }

  selectedAcceptKeys(): string[] {
    return Object.keys(this.acceptKeys).filter((key) => this.acceptKeys[key]);
  }

  toggleAcceptAll(checked: boolean): void {
    this.acceptAllChanges = checked;
    Object.keys(this.acceptKeys).forEach((key) => {
      this.acceptKeys[key] = checked;
    });
  }

  applyImport(): void {
    if (!this.file) {
      return;
    }
    this.busy = true;
    this.catalog
      .apply(this.data.vendorId, this.file, {
        fieldMap: this.fieldMap,
        acceptKeys: this.selectedAcceptKeys(),
        acceptAllChanges: this.acceptAllChanges,
        catalogRefreshMonths: this.refreshMonths,
      })
      .subscribe({
        next: (result) => {
          this.busy = false;
          this.result = result;
          this.step = 'done';
          this.reloadHistory();
          this.notification.show('success', 'Catalog import finished');
        },
        error: (err) => {
          this.busy = false;
          this.notification.show(
            'error',
            err?.error?.message || 'Could not import the catalog'
          );
        },
      });
  }

  saveRefreshOnly(): void {
    this.catalog
      .saveSettings(this.data.vendorId, {
        catalogRefreshMonths: this.refreshMonths,
        catalogFieldMap: this.fieldMap,
      })
      .subscribe({
        next: () => {
          this.notification.show('success', 'Catalog refresh settings saved');
        },
        error: (err) => {
          this.notification.show(
            'error',
            err?.error?.message || 'Could not save catalog settings'
          );
        },
      });
  }

  close(reload = false): void {
    this.dialogRef.close({ reload, result: this.result });
  }

  formatDate(value: any): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? '' : date.toLocaleString();
  }

  changeSummary(row: any): string {
    const changes = row?.changes || {};
    return Object.keys(changes)
      .map((key) => `${key}: ${changes[key]?.previous ?? '—'} → ${changes[key]?.next ?? '—'}`)
      .join('; ');
  }
}
